<template>

    <div
        class="order"
        :class="{ 'closed': !curShift }"
    >
        <!-- Loading Overlay -->
        <v-overlay :value="busy" :z-index="999999">
            <v-progress-circular indeterminate size="44" />
        </v-overlay>

        <div v-if="!curShift" class="alert-container">
            <v-alert
                v-if="!!curCashRegister"
                class="alert"
                border="left"
                icon="fal fa-calendar"
                prominent
                type="error"
            >
                <div>{{ $t('allegra.order.shift_closed') }}</div>
                <div class="d-flex">
                    <v-btn
                        class="mt-3"
                        outlined
                        @click="eventBus.$emit('order-toggle-shift')"
                    >
                        {{ $t('allegra.order.shift_open') }}
                    </v-btn>
                </div>
            </v-alert>
        </div>

        <template v-else>
            <!-- Search Products -->
            <div class="search-product-container">
                <search-product
                    ref="searchProduct"
                    height="52px"
                    solo
                    hide-details
                    :preserve-selection="false"
                    :products="products"
                    :disable-keys="!mainScreenActive"
                    :disabled="!curShift"
                    @not-found="onProductNotFound"
                    @select="selectProduct($event.product, $event.quantity)"
                >
                    <template slot="append">
                        <v-btn
                            class="mt-1 mr-1"
                            large
                            icon
                            :disabled="!curShift"
                            @click="show.numpad = !show.numpad"
                        >
                            <v-icon size="26">fal fa-keyboard</v-icon>
                        </v-btn>

                        <v-btn
                            class="mt-1 mr-1"
                            large
                            icon
                            :disabled="!curShift"
                            @click="show.selectProducts = true"
                        >
                            <v-icon size="26">fal fa-plus-circle</v-icon>
                        </v-btn>
                    </template>
                </search-product>
            </div>

            <!-- Order Items -->
            <order-items
                ref="orderItems"
                @delete-item="deleteItem($event.item)"
                @toggle-dialog="toggleDialog({ dialog: $event.dialog })"
            />

            <v-alert
                v-if="orderMsg"
                class="order-alert"
                type="warning"
                text
                transition="slide-y-transition"
            >
                {{ orderMsg }}
            </v-alert>

            <v-alert
                v-else-if="certExpiration"
                class="order-alert"
                type="error"
                transition="slide-y-transition"
            >
                {{ certExpiration.msg }}
                <v-btn
                    v-if="certExpiration.addNew"
                    class="ml-2"
                    rounded
                    outlined
                    @click="$router.push({ name: 'adminAccount' }).catch(err => {})"
                >
                    {{ $t('general.add_new') }}
                </v-btn>
            </v-alert>
        </template>

        <!-- Numpad Dialog -->
        <numpad-expanded
            :show.sync="show.numpad"
            type="text"
            :title="$t('allegra.order.search_by_code')"
            :value="0"
            sign-button-replace="*"
            @confirm="searchProduct('' + $event.value)"
        />

        <!-- Cash Dialog -->
        <numpad-expanded
            :show.sync="show.cash"
            type="cash"
            :persist="true"
            :title="$t('general.cash')"
            :value="order ? order.getLeftToPay().toRoundedUnit(2) : 0"
            :total="order ? order.getLeftToPay().toRoundedUnit(2) : 0"
            @confirm="initOrderAction({
                paymentType: 'cash',
                value: $event.value,
                flag: !$event.addToDebt ? 'debt,skip' : null
            })"
        />

        <!-- Credit Card Dialog -->
        <numpad-expanded
            :show.sync="show.creditCard"
            type="creditCard"
            :title="`${$t('general.credit_card')} / ${$t('general.cash')}`"
            :value="0"
            :total="order ? order.getLeftToPay().toRoundedUnit(2) : 0"
            :max="order ? order.getLeftToPay().toRoundedUnit(2) : 0"
            @confirm="initOrderAction({
                paymentType: 'credit_card',
                value: $event.value > order.getLeftToPay().toRoundedUnit(2) ? $event.value : $event.calculated
            })"
        />

        <!-- Advance Dialog -->
        <numpad-expanded
            :show.sync="show.advance"
            type="advance"
            :title="$t('general.advance')"
            :value="order ? order.getLeftToPay().toRoundedUnit(2) : 0"
            :total="order ? order.getLeftToPay().toRoundedUnit(2) : 0"
            :max="order ? order.getLeftToPay().toRoundedUnit(2) : 0"
            @confirm="initOrderAction({
                paymentType: 'advance',
                value: $event.payments,
                flag: $event.paidDate ? 'advancePaidDate,' + $event.paidDate : null
            })"
        />

        <!-- Installments Dialog -->
        <numpad-expanded
            :show.sync="show.installments"
            type="otherNoncash"
            :title="$t('general.installments')"
            :value="0"
            :total="order ? order.getLeftToPay().toRoundedUnit(2) : 0"
            :max="order ? order.getLeftToPay().toRoundedUnit(2) : 0"
            @confirm="initOrderAction({
                paymentType: 'other_noncash',
                value: $event.value,
                flag: 'type,installments'
            })"
        />

        <!-- Other NonCash Dialog -->
        <numpad-expanded
            :show.sync="show.otherNoncash"
            type="otherNoncash"
            :title="$t('general.other_noncash')"
            :value="0"
            :total="order ? order.getLeftToPay().toRoundedUnit(2) : 0"
            :max="order ? order.getLeftToPay().toRoundedUnit(2) : 0"
            @confirm="initOrderAction({
                paymentType: 'other_noncash',
                value: $event.value
            })"
        />

        <!-- Mixed Payments Dialog -->
        <numpad-expanded
            :show.sync="show.mixedPayment"
            type="mixedPayment"
            :title="$t('general.mixed')"
            :value="0"
            :total="order ? order.getLeftToPay().toRoundedUnit(2) : 0"
            @confirm="initOrderAction({
                paymentType: 'mixed_payment',
                value: $event.payments
            })"
        />

        <!-- Discount Dialog -->
        <numpad-expanded
            :show.sync="show.discount"
            type="discount"
            :title="$t('allegra.order.discount')"
            :value="0"
            :total="order ? order.getPrice().toRoundedUnit(2) : 0"
            :max="99"
            @confirm="giveDiscount($event.value)"
        />

        <!-- Select Client Dialog -->
        <select-client
            :show.sync="show.selectClient"
            :client-id="order ? order.client_id : null"
            @select="selectClient"
        />

        <!-- Select Products Dialog -->
        <select-products
            :show.sync="show.selectProducts"
            @select="selectProducts($event)"
        />

        <!-- Pay By Invoice Dialog -->
        <invoice
            :show.sync="show.invoice"
            :order="order"
            mode="default"
            :preview="true"
            @pay-order="initOrderAction({
                paymentType: $event.paymentType,
                registerType: einvoiceOnly ? 'manual' : null,
                ...$event
            })"
        />

        <!-- Proforma Invoice Dialog -->
        <invoice
            :show.sync="show.invoiceProforma"
            :order="order"
            mode="proforma"
            :preview="true"
            @save-order="saveAsProforma"
        />

        <!-- Pay By Einvoice Dialog -->
        <invoice
            :show.sync="show.einvoice"
            :order="order"
            mode="default"
            :is-einvoice="true"
            :preview="true"
            @pay-order="initOrderAction({
                paymentType: $event.paymentType,
                registerType: 'einvoice',
                ...$event
            })"
        />

        <!-- Discount Window -->
        <numpad-expanded
            :show.sync="show.itemDiscount"
            type="discount"
            :title="$t('general.discount_item')"
            :value="curOrderItem ? curOrderItem.getDiscountPercent() : 0"
            :total="curOrderItem ? curOrderItem.getPrice().toRoundedUnit(2) : 0"
            :max="99"
            @confirm="giveItemDiscount(curOrderItem, $event.value)"
        />

        <!-- Quantity Window -->
        <numpad-expanded
            :show.sync="show.itemQuantity"
            type="count"
            :title="$t('general.quantity')"
            :value="curOrderItem ? curOrderItem.getCount() : 0"
            @confirm="updateItemCount(curOrderItem, $event.value)"
        />

        <!-- Item Price Window -->
        <numpad-expanded
            :show.sync="show.itemUnitPrice"
            type="price"
            :title="$t('general.price')"
            :value="curOrderItem ? curOrderItem.getUnitPrice().toRoundedUnit(3) : 0"
            @confirm="updateItemUnitPrice(curOrderItem, $event.value)"
        />

        <!-- Select Exempt From Tax -->
        <select-exempt-from-tax
            :show.sync="show.itemExemptFromTax"
            :order="order"
            :order-item="curOrderItem"
            @save="setItemExemptFromTax($event.item, $event.exemptFromTaxReason)"
        />

        <!-- Item Comments Window -->
        <select-order-item-comments
            :show.sync="show.itemComments"
            :order-item="curOrderItem"
            @save="setItemComments($event.item, [$event.comment])"
        />

        <!-- Other Payment Methods Dialog -->
        <payment-methods
            :show.sync="show.paymentMethods"
            @toggle-dialog="toggleDialog"
            @pay="initOrderAction({ paymentType: $event.paymentType, flag: $event.flag })"
        />

        <!-- Receipt Note -->
        <note
            :show.sync="show.note"
            :note="order ? order.note : null"
            @save="setNote($event.note)"
        />

        <!-- Add Reference Dialog -->
        <order-reference
            :show.sync="show.orderReference"
            @save="setCustomReference($event.refOrder, $event.refTime)"
        />
    </div>
</template>

<script>
import NumpadExpanded from '@/components/NumpadExpanded';

import Invoice from '@/views/shared/components/Invoice';
import PaymentMethods from '@/views/shared/components/PaymentMethods';
import OrderReference from '@/views/shared/components/OrderReference';
import SelectClient from '@/views/shared/components/SelectClient';
import Note from '@/views/shared/components/Note';
import SearchProduct from '@/views/shared/components/SearchProduct';
import OrderItems from '@/views/allegra/order/OrderItems';
import SelectProducts from '@/views/allegra/order/SelectProducts';
import SelectExemptFromTax from '@/views/allegra/order/SelectExemptFromTax';
import SelectOrderItemComments from '@/views/allegra/order/SelectOrderItemComments';

import Product from '@/models/Product';
import Order from '@/models/Order';
import User from '@/models/User';
import Role from '@/models/Role';
import Client from '@/models/Client';
import Shift from '@/models/Shift';
import Shortcut from '@/models/Shortcut';
import CashRegister from '@/models/CashRegister';
import Notification from '@/models/Notification';

import FiscalService from '@/services/FiscalService';
import ApiService from '@/services/ApiService';
import LogService from '@/services/LogService';
import EinvoiceService from '@/services/EinvoiceService';
import ErrorReporter from '@/wrappers/ErrorReporter';
import CardReadService from '@/wrappers/CardReadService';
import * as mutationTypes from '@/store/mutationTypes';
import { mapGetters } from 'vuex';
import latToCyr from '@/utilities/LatToCyr'

export default {
    name: 'Order',

    components: {
        NumpadExpanded,
        SelectClient,
        Note,
        Invoice,
        PaymentMethods,
        OrderItems,
        SelectProducts,
        SelectExemptFromTax,
        SelectOrderItemComments,
        SearchProduct,
        OrderReference,
    },

    async created() {
        this.drawer = {
            show: !this.$vuetify.breakpoint.xsOnly,
            mini: !this.$vuetify.breakpoint.xsOnly,
        }

        document.addEventListener('keypress', this.backgroundKeyPressHandler);
        document.addEventListener('keyup', this.backgroundKeyUpHandler);

        // Events coming from the navigation drawer
        this.eventBus.$on('drawer-toggle', this.updateDrawer)
        this.eventBus.$on('issue-receipt', this.createNewReceipt)
        this.eventBus.$on('orders-refresh', this.loadData)

        // Update the show property when another component opened a screen / modal
        this.eventBus.$on('show-screens', this.onShowScreens)

        // Events coming from the bottom navigation
        this.eventBus.$on('order-bottom-sheet', this.toggleBottomSheet);
        this.eventBus.$on('order-init-action', this.initOrderAction);
        this.eventBus.$on('order-cancel-order', this.cancelOrder);
        this.eventBus.$on('order-pause-order', this.pauseOrder);
        this.eventBus.$on('order-toggle-dialog', this.toggleDialog);

        this.eventBus.$on('card-read-completed', this.onReadCardCompleted);

        // activated
        let curOrderInStore = null
        if(this.curOrder) {
            curOrderInStore = Order.find(this.curOrder.hash)
        }

        this.order = !curOrderInStore || !curOrderInStore.id ? this.curOrder : null
        this.$store.commit(mutationTypes.SET_CUR_ORDER, { curOrder: this.order })

        const selectedItem = this.order && this.order.items.length ? this.order.items[this.order.items.length - 1] : null
        this.setSelectedItem(selectedItem)

        let products = Product.all()
            .filter(product => product.is_sold)
            .map(product => new Product(product))

        this.products = Object.freeze(products)

        this.loadData()

        this.shortcuts = Shortcut.all()

        this.checkNotifications()

        this.sdcUiData = (await FiscalService.getUiData('ESDC')).data || null
    },

    beforeDestroy() {
        document.removeEventListener('keypress', this.backgroundKeyPressHandler);
        document.removeEventListener('keyup', this.backgroundKeyUpHandler);

        // Events coming from the navigation drawer
        this.eventBus.$off('drawer-toggle', this.updateDrawer)
        this.eventBus.$off('issue-receipt', this.createNewReceipt)
        this.eventBus.$off('orders-refresh', this.loadData)

        // Update the show property when another component opened a screen / modal
        this.eventBus.$off('show-screens', this.onShowScreens)

        // Events coming from the bottom navigation
        this.eventBus.$off('order-bottom-sheet', this.toggleBottomSheet);
        this.eventBus.$off('order-init-action', this.initOrderAction);
        this.eventBus.$off('order-cancel-order', this.cancelOrder);
        this.eventBus.$off('order-pause-order', this.pauseOrder);
        this.eventBus.$off('order-toggle-dialog', this.toggleDialog);
        this.eventBus.$off('card-read-completed', this.onReadCardCompleted)
    },

    mounted() {
        if(this.curShift && this.curCashRegister && this.curCashRegister.depositRequired()) {
            this.eventBus.$emit('cash-register-deposit')
        }
    },

    data: () => ({
        busy: false,

        drawer: {},

        order: null,
        products: [],
        shortcuts: [],

        show: {
            numpad: false,
            cash: false,
            creditCard: false,
            installments: false,
            otherNoncash: false,
            mixedPayment: false,
            invoice: false,
            einvoice: false,
            advance: false,
            paymentMethods: false,
            invoiceProforma: false,
            discount: false,
            selectClient: false,
            selectProducts: false,
            itemDiscount: false,
            itemQuantity: false,
            itemUnitPrice: false,
            itemComments: false,
            itemExemptFromTax: false,
            bottomSheet: false,
            cashiers: false,
            shift: false,
            staff: false,
            cashRegister: false,
            reports: false,
            receipts: false,
            cashRegisterDeposit: false,
            cashRegisterCashOut: false,
            deposit: false,
            sdcManagement: false,
            orderReference: false,
            note: false,
            externalDisplayTimeout: null,
            sdcUiData: null,
        },
    }),

    watch: {
        curShift(val) {
            if (!val) {
                this.order = null;
            }
        },

        order: {
            deep: true,
            handler(val, oldVal) {
                this.$store.commit(mutationTypes.SET_CUR_ORDER, { curOrder: val });

                if(!val) {
                    this.setSelectedItem(null)
                }

                this.sendToExternalDisplay(val || oldVal, !val && !!oldVal)
            }
        },
    },

    computed: {
        ...mapGetters([
            'curOrder',
            'curOrderItem',
            'unpaidOrders',
            'trainingMode',
            'pausedOrder',
            'pendingCardPayment'
        ]),

        curShift() {
            return Shift.current();
        },

        curCashRegister() {
            return CashRegister.current();
        },

        mainScreenActive() {
            return Object.keys(this.show).every(key => !this.show[key])
        },

        orderEmpty() {
            return !this.order || !this.order.items || !this.order.items.length;
        },

        orderMsg() {
            if(this.trainingMode) {
                return this.$t('general.training_active')
            }

            if(!this.order) return null;

            const reverseCalculation = !!this.tenant.venue.settings.reverse_calculation

            if(this.order.export) {
                let message = this.$t('invoice.warning.export')

                if(!reverseCalculation) {
                    message += ' ' + this.$t('order.zero_tax_prices')
                }
                message = message + ' ' + this.$t('order.zero_tax')
                return message
            }

            if(this.order.client_id) {
                const client = Client.find(this.order.client_id)
                if(client) {
                     if(client.isDiplomat()) {
                        let message = this.$t('order.zero_tax')

                        if(!reverseCalculation) {
                            message = this.$t('order.zero_tax_prices') + ' ' + message
                        }
                        return message
                    }

                    const localCountry = (locale === 'me' && !client.country || client.country === 'MNE')
                        || (locale === 'sr' && client.country === 'SRB')
                        || (locale === 'ba' && client.country === 'BIH')

                    if(!localCountry) {
                        return this.$t('invoice.warning.not_export_invoice')
                    }
                }
            }

            return null
        },

        certExpiration() {
            if(this.tenant?.cert_expire_at && moment(this.tenant.cert_expire_at).diff(moment(), 'days') < 30) {
                return {
                    addNew: this.allows('nav_access_admin'),
                    msg: this.$t('general.certificate_expires', { value: moment(this.tenant.cert_expire_at).format('DD.MM.YYYY.') })
                }
            }

            if(!FiscalService.useDevEsdc && this.sdcUiData?.certExpDate && moment(this.sdcUiData.certExpDate).diff(moment(), 'days') < 30) {
                const expired = moment(this.sdcUiData.certExpDate).diff(moment(), 'days') <= 0
                return {
                    addNew: false,
                    msg: this.$t(
                        expired ? 'sdc.esdc.certificate_expired_message' : 'sdc.esdc.certificate_expires_message',
                        { value: moment(this.sdcUiData.certExpDate).format('DD.MM.YYYY.') }
                    )
                }
            }

            return null
        },

        noncashOnly() {
            return this.tenant && this.tenant.venue && !!this.tenant.venue.settings.noncash_only
        },

        einvoiceEnabled() {
            return EinvoiceService.isEnabled();
        },

        einvoiceOnly() {
            return EinvoiceService.einvoiceOnly();
        },

        inventoryAccountingEnabled() {
            return !!this.tenant.venue?.settings?.use_inventory_accounting;
        },

        inventoryAccountingPriceChangeEnabled() {
            return !!this.tenant.venue?.settings?.inventory_accounting_price_change_enabled;
        },
    },

    methods: {
        loadData() {
            if (!this.isOnline) return

            const ordersLastUpdatedAt = Order.getLastUpdatedAt();
            const productsLastUpdatedAt = Product.getLastUpdatedAt();

            const params = {
                orders_last_updated_at: ordersLastUpdatedAt ? ordersLastUpdatedAt.utc().format('YYYY-MM-DD HH:mm:ss') : null,
                products_last_updated_at: productsLastUpdatedAt ? productsLastUpdatedAt.utc().format('YYYY-MM-DD HH:mm:ss') : null,
            }

            ApiService.get('/allegra/load/orders', { params: params }).then(response => {
                Order.insert({ data: response.data.orders, freeze: true });
                Product.insert({ data: response.data.products, freeze: true });
                Shift.create({ data: [response.data.shift] });
                Client.create({ data: response.data.clients });
                // User.create({ data: response.data.users });
                // Role.create({ data: response.data.roles });

                let products = Product.all()
                    .filter(product => product.is_sold)
                    .map(product => new Product(product))

                this.products = Object.freeze(products)
            });
        },

        backgroundKeyPressHandler(event) {
            if(!this.mainScreenActive) {
                return
            }

            if (this.$refs.searchProduct) {
                this.$refs.searchProduct.focus()
            }
        },

        backgroundKeyUpHandler(event) {
            if(!this.mainScreenActive) {
                event.preventDefault()
                event.stopPropagation()
                return
            }

            const shortcut = this.shortcuts.find(shortcut => shortcut.value === event.code)
            if(shortcut) {
                event.preventDefault()
                event.stopPropagation()
                this.handleShortcut(shortcut)
            }
        },

        handleShortcut(shortcut) {
            if(!shortcut) return

            if(shortcut.name === 'open_drawer') {
                this.openDrawer()
                return
            }

            if(this.orderEmpty || !this.curShift || this.busy) return

            switch(shortcut.name) {
                case 'order_item_count':
                    this.toggleDialog({ dialog: 'itemQuantity' })
                    break
                case 'order_item_price':
                    if(this.priceUpdateDisabled(this.curOrderItem)) return
                    this.toggleDialog({ dialog: 'itemUnitPrice' })
                    break
                case 'order_item_discount':
                    this.toggleDialog({ dialog: 'itemDiscount' })
                    break
                case 'order_item_delete':
                    this.deleteItem(this.curOrderItem)
                    break
                case 'order_item_delete_last': {
                    const lastItem = this.order && this.order.items.length
                        ? this.order.items[this.order.items.length - 1]
                        : null
                    this.deleteItem(lastItem)
                    break
                }
                case 'order_cancel':
                    this.cancelOrder()
                    break
                case 'order_pay_cash':
                    if(this.tenant.venue.settings.cash_change_enabled) {
                        this.toggleDialog({ dialog: 'cash' })
                    } else {
                        this.initOrderAction({ paymentType: 'cash' })
                    }
                    break
                case 'order_pay_credit_card':
                    this.toggleDialog({ dialog: 'creditCard' })
                    break
                case 'order_pay_invoice':
                    this.toggleDialog({ dialog: 'invoice' })
                    break
                case 'order_pay_credit_card_business':
                    this.initOrderAction({ paymentType: 'credit_card_business' })
                    break
                case 'order_pay_order':
                    this.initOrderAction({ paymentType: 'order' })
                    break
                case 'order_proforma':
                    this.toggleDialog({ dialog: 'invoiceProforma' })
                    break
                case 'order_customer':
                    this.toggleDialog({ dialog: 'selectClient' })
                    break
                default:
                    break
            }
        },

        searchProduct(query) {
            setTimeout(() => {
                if (this.$refs.searchProduct) {
                    this.$refs.searchProduct.focus()
                    this.$refs.searchProduct.search = query
                }
            }, 100)
        },

        selectProducts(selected) {
            selected.forEach(item => {
                this.selectProduct(item.product, item.quantity)
            });
        },

        selectProduct(product, quantity) {
            if(!this.order) {
                this.order = Order.createNew(this.user);
            }

            const selectedItem = this.order.addItem(product, quantity);
            if(!selectedItem) {
                if(!this.order.items.length) {
                    this.order = null;
                }

                return;
            }

            this.$refs.orderItems.onItemSelect(selectedItem)
        },

        onProductNotFound() {
            const audio = new Audio('/audio/alert1.mp3');
            audio.play();

            this.$confirm(this.$t('allegra.order.product_not_found'), {
                buttonTrueText: this.$t('general.continue'),
                icon: 'fal fa-times',
                color: 'error',
                title: this.$t('general.error'),
                persistent: true,
            }, async result => {});
        },

        setItemExemptFromTax(item, exemptFromTaxReason) {
            this.order.setItemExemptFromTax(item, exemptFromTaxReason)
        },

        setNote(note) {
            this.order.setNote(note);
        },

        setItemComments(item, comments) {
            this.order.setItemComments(item, comments);
        },

        /**
         * Fire issue or pay order action
         */
        async initOrderAction(options = {}) {
            const { paymentType, value, mode } = options

            if(this.noncashOnly && !['invoice', 'credit_card_business', 'order', 'advance'].includes(paymentType)) return

            if(this.curCashRegister.depositRequired()) {
                this.eventBus.$emit('cash-register-deposit')
                return
            }

            if(['order', 'advance'].includes(paymentType) && !this.order.client_id) {
                this.show.selectClient = true;
                this.$snackbar(this.$t('order.buyer_select'), 'error');
                return;
            }

            if(this.order) {
                const hasItemsWithNegativeCount = this.order.items.some(item => item.getCount() < 0);
                if(hasItemsWithNegativeCount && (!this.order.flag || !this.order.flag.startsWith('ref,'))) {
                    this.show.orderReference = true;
                    return;
                }
            }

            if(paymentType !== 'order' && !this.curCashRegister.allowsOrderPayment(paymentType, value)) {
                this.$snackbar(this.$t('order.error.payment_type_not_allowed'), 'error');
                return;
            }

            const unpaidOrders = mode === 'summary' ? [...this.unpaidOrders] : []
            if(CardReadService.shouldReadCard(this.order, unpaidOrders, options)) {
                this.readCard(options)
                return
            }

            await this.executePay(options)
        },

        async executePay(options) {
            const { paymentType, mode } = options

            this.busy = true

            try {
                if(paymentType === 'order') {
                    await this.issueOrder()
                    this.busy = false
                    return
                }

                if(mode === 'summary') {
                    await this.payAllOrders(options)
                } else {
                    await this.payOrder(options)
                }
            } catch(error) {
                console.log(error.message)

                this.$snackbar(this.$t('general.error_contact_support'), 'error', {
                    mode: 'multi-line',
                    timeout: 10000,
                    title: this.$t('order.error.title')
                })

                const orderToLog = this.order ? this.order.$duplicate() : null
                if(orderToLog) {
                    orderToLog.items = JSON.stringify(orderToLog.items)
                    orderToLog.taxes = JSON.stringify(orderToLog.taxes)
                }

                ErrorReporter.reportMessage('Order action exception', {
                    error: error.message,
                    order: orderToLog,
                })
            } finally {
                this.busy = false
            }
        },

        async readCard(options) {
            const { mode } = options

            this.$confirm(
                this.$t('order.card_read_warning'),
                {
                    buttonTrueText: this.$t('general.yes'),
                    buttonCancelText: this.$t('general.no'),
                    icon: null,
                    color: 'primary',
                    title: this.$t('general.credit_card'),
                    persistent: true,
                },
                async result => {
                    if(!result) {
                        await this.executePay(options)
                        return
                    }

                    const unpaidOrders = mode === 'summary' ? [...this.unpaidOrders] : []
                    const startCardReadResult = await CardReadService.startCardRead(this.order, unpaidOrders, options)
                    if(startCardReadResult.success) return

                    this.$confirm(this.$t(`order.error.card_read.${startCardReadResult.error}`), {
                        icon: 'fal fa-exclamation-triangle',
                        color: 'error',
                        title: this.$t('general.action_failed')
                    }, async result => {});
                }
            )
        },

        async onReadCardCompleted(result) {
            if(result.success && this.pendingCardPayment && this.pendingCardPayment.paymentSessionToken === result.paymentSessionToken) {
                this.order = this.pendingCardPayment.order

                let flag = this.pendingCardPayment.options.flag
                const options = { ...this.pendingCardPayment.options, flag: flag ? flag + '|payment,card' : 'payment,card' }

                await this.executePay(options)
            } else {
                this.$confirm(this.$t(`order.error.card_read.${result.error}`), {
                    icon: 'fal fa-exclamation-triangle',
                    color: 'error',
                    title: this.$t('general.action_failed')
                }, async result => {});
            }
        },

        /**
         * Issue single order
         */
        async issueOrder(options = {}) {
            const result = await this.order.$duplicate().issue({
                user: this.user,
                choosePrinter: options.choosePrinter
            })

            this.onOrderIssue(result)
        },

        /**
         * Pay single order
         */
        async payOrder(options = {}) {
            const {
                paymentType,
                value,
                internalNumber,
                choosePrinter,
                flag,
                forceInvoiceForm,
                registerType = null,
                registerRequest = null
            } = options

            if(!this.order || !this.order.items.length) return

            this.order.internal_number = internalNumber || null

            const result = await this.order.$duplicate().pay({
                user: this.user,
                paymentType: paymentType,
                value: value,
                choosePrinter: choosePrinter,
                forceInvoiceForm: forceInvoiceForm,
                flag: flag,
                openDrawer: true,
                registerType: registerType,
                registerRequest: registerRequest
            })

            this.onOrderIssue(result)
        },

        /**
         * Pay all orders
         */
        async payAllOrders(options = {}) {
            const {
                paymentType,
                value,
                internalNumber,
                choosePrinter,
                flag,
                forceInvoiceForm,
                registerType = null
            } = options

            const orders = [...this.unpaidOrders]

            const result = await Order.payAll(orders, {
                user: this.user,
                table: orders[0].table,
                client: orders[0].client,
                internalNumber: internalNumber,
                paymentType: paymentType,
                value: value,
                choosePrinter: choosePrinter,
                forceInvoiceForm: forceInvoiceForm,
                flag: flag,
                openDrawer: true,
                registerType: registerType
            })

            this.$store.commit(mutationTypes.SET_CUR_UNPAID_ORDERS, { curUnpaidOrders: [] });

            this.onOrderIssue(result, false)
        },

        /**
         * On order issue
         */
        onOrderIssue(result, resetOrder = true) {
            const successMsg = this.order?.isEinvoice() ? this.$t('einvoice.issued') : this.$t('order.issued');

            if(result.status === Order.SYNC_STATUS_FAILED) {
                this.$confirm(result.error, {
                    icon: 'fal fa-exclamation-triangle',
                    color: 'error',
                    title: this.$t('order.error.title'),
                }, async result => {});

                return
            }

            if(result.status === Order.SYNC_STATUS_SUCCESS) {
                this.$snackbar(successMsg, 'success', { timeout: 3000 })
            }

            if(result.status === Order.SYNC_STATUS_SUCCESS_WITH_ERRORS) {
                this.$confirm(result.error || result.efiError || result.printError, {
                    icon: 'fal fa-exclamation-circle',
                    color: 'warning',
                    title: successMsg,
                }, async result => {});
            }

            if(resetOrder) {
                this.order = null
            }
        },

        /**
         * Save order as proforma
         */
        async saveAsProforma(options = {}) {
            const { internalNumber, choosePrinter, paymentType, forceInvoiceForm } = options

            this.order.internal_number = internalNumber || null
            const result = await this.order.$duplicate().proforma({
                user: this.user,
                paymentType: paymentType,
                choosePrinter: choosePrinter,
                forceInvoiceForm: forceInvoiceForm,
            });

            if(result.status === Order.SYNC_STATUS_FAILED) {
                this.$confirm(result.error, {
                    icon: 'fal fa-times',
                    color: 'error',
                    title: this.$t('order.error.title'),
                }, async result => {});

                return
            }

            this.$snackbar(this.$t('allegra.order.saved_proforma'));
            this.order = null;
        },

        /**
         * Cancel order
         */
        cancelOrder() {
            LogService.info({
                type: 'cancel_receipt',
                value: this.order?.getToPay().toFormat('0.00') || '0.00'
            });

            this.$snackbar(this.$t('allegra.order.canceled'));
            this.order = null;
            this.setSelectedItem(null);
        },

        pauseOrder(pause) {
            if(pause) {
                this.$store.commit(mutationTypes.SET_PAUSED_ORDER, { pausedOrder: this.order.$duplicate() });
                this.order = null;
                this.$snackbar(this.$t('general.receipt_paused'), 'info');
            } else {
                this.order = this.pausedOrder.fresh(this.user);
                this.$store.commit(mutationTypes.SET_PAUSED_ORDER, { pausedOrder: null });
                this.$snackbar(this.$t('general.receipt_continued'), 'info');
            }
        },

        createNewReceipt(order) {
            this.order = order;
        },

        giveDiscount(value) {
            const uniqueDiscounts = this.order.items
                .map(item => item.getDiscountPercent())
                .filter((value, index, array) => index === array.indexOf(value))

            if(uniqueDiscounts.length > 1) {
                this.$snackbar(this.$t('order.discount_not_possible'), 'error', { timeout: 10000 });
                return
            }

            this.order.giveDiscount(value);
        },

        selectClient(client) {
            this.order.setClient(client);
        },

        deleteItem(orderItem) {
            if(!orderItem) return;

            LogService.info({
                type: 'cancel_item',
                value: orderItem?.getToPay().toFormat('0.00') || '0.00'
            });

            this.order.deleteItem(orderItem);

            if(this.curOrderItem && orderItem.hash === this.curOrderItem.hash) {
                this.setSelectedItem(null)
            }

            if(this.order.items.length === 0) {
                this.order = null
            }
        },

        updateItemCount(orderItem, count) {
            this.order.updateItemCount(orderItem, count);
        },

        updateItemUnitPrice(orderItem, price) {
            this.order.updateItemUnitPrice(orderItem, price);
        },

        giveItemDiscount(orderItem, percent) {
            this.order.giveItemDiscount(orderItem, percent);
        },

        setCustomReference(refOrder, refTime) {
            this.order.setCustomReference(refOrder, refTime);
        },

        updateDrawer(drawer) {
            this.drawer = drawer
        },

        onShowScreens(screens) {
            Object.assign(this.show, screens)
        },

        toggleBottomSheet(show) {
            this.show.bottomSheet = show
        },

        toggleDialog(data) {
            this.show[data.dialog] = !this.show[data.dialog]
        },

        setSelectedItem(item) {
            this.$store.commit(mutationTypes.SET_CUR_ORDER_ITEM, { curOrderItem: item });
        },

        openDrawer() {
            if(window.electron) {
                this.$printService.openDrawer()
            }
        },

        async checkNotifications() {
            await Notification.fetchUnread()
            const unreadNotifications = Notification.unread()
            if(unreadNotifications.length) {
                this.eventBus.$emit('show-notification', { notification: unreadNotifications[0] });
            }
        },

        sendToExternalDisplay(order, isClosingOrder = false) {
            if(!window.electron) return

            if(isClosingOrder) {
                if(order) {
                    const toPayLine = [
                        this.sanitizeText(this.$t('general.total').toUpperCase() + ':'),
                        this.sanitizeText(order.getLeftToPay().toFormat('0.00'))
                    ]

                    window.electron.invoke('external-display:write-text', toPayLine, []);
                }

                this.externalDisplayTimeout = setTimeout(() => {
                    window.electron.invoke('external-display:write-text', [`  *** ${this.$t('general.thank_you').toUpperCase()} ***`], []);
                    this.externalDisplayTimeout = setTimeout(() => window.electron.invoke('external-display:clear'), 3000)
                }, 5000)

                return
            }

            clearTimeout(this.externalDisplayTimeout)

            const toPayLine = [
                this.sanitizeText(this.$t('general.total').toUpperCase() + ':'),
                this.sanitizeText(order.getLeftToPay().toFormat('0.00'))
            ]
            const lastItem = order.items[order.items.length - 1]
            const itemLine = lastItem
                ? [this.sanitizeText(lastItem.name), this.sanitizeText(lastItem.getToPay().toFormat('0.00'))]
                : []

            window.electron.invoke('external-display:write-text', toPayLine, itemLine);
        },

        priceUpdateDisabled(item) {
            const product = item.getProduct();
            if(!product) return true;

            return !!product.is_goods && (this.inventoryAccountingEnabled && !this.inventoryAccountingPriceChangeEnabled);
        },

        sanitizeText(text) {
            const latinText = latToCyr(text, 'lat')
            return latinText.replace(/č/g, 'c')
                .replace(/Č/g, 'C')
                .replace(/ć/g, 'c')
                .replace(/Ć/g, 'C')
                .replace(/ž/g, 'z')
                .replace(/Ž/g, 'Z')
                .replace(/đ/g, 'dj')
                .replace(/Đ/g, 'Dj')
                .replace(/š/g, 's')
                .replace(/Š/g, 'S')
        }
    }
};
</script>

<style lang="scss" scoped>
.order {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    background: #ffffff;
    position: relative;

    .alert-container {
        display: flex;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #d1d4dc;

        .alert {
            margin: 0 10px;
            max-width: 350px;
        }
    }

    .search-product-container {
        background: white;
        position: sticky;
        width: 100%;
        top: 56px;
        z-index: 1;

        @media screen and (min-width: 962px) {
            top: 60px;
        }
    }

    .order-alert {
        position: fixed;
        bottom: 196px;
        right: 12px;
        margin-left: 12px;
        z-index: 5;
    }
}
</style>
